import { useTranslation } from '@packlink/translation-provider';
import { Dialog, DialogHeader, Typography } from '@shipengine/giger';
import {
    getCancelDialogHeaderStyles,
    getCancelDialogHeaderTitleStyles,
    getCancelDialogStyles,
} from './CancelDialogStyles';
import { SubscriptionCancellationSuccess } from './SubscriptionCancellationSuccess';
import { SubscriptionCancellationConfirmation } from './SubscriptionCancellationConfirmation';
import { useCancelClientSubscription } from '../../hooks/useCancelClientSubscription';
import { useState } from 'react';
import { RetentionDiscount } from './RetentionDiscount';
import { useRetentionDiscount } from '@Subscriptions/hooks/useRetentionDiscount';
import { RetentionDiscountSuccess } from '@Subscriptions/components/CancelDialog/RetentionDiscountSuccess';

export interface ICancelDialogProps {
    isOpen: boolean;
    planName: string;
    onClose: () => void;
    expirationDate?: string;
}

export function CancelDialog({ isOpen, planName, onClose, expirationDate = '' }: ICancelDialogProps): JSX.Element {
    const { t } = useTranslation();
    const { cancelClientSubscription, isSuccess, isPending } = useCancelClientSubscription();
    const [showDiscountDialog, setShowDiscountDialog] = useState(false);

    const resetDialogAndClose = () => {
        setShowDiscountDialog(false);
        onClose();
    };

    const {
        applyRetentionDiscount,
        pendingApplication,
        discountValue,
        discountedPrice,
        startDate,
        discountApplied,
        fullPrice,
        discountCurrency,
    } = useRetentionDiscount(resetDialogAndClose);

    return (
        <Dialog css={getCancelDialogStyles} isOpen={isOpen}>
            <DialogHeader
                css={getCancelDialogHeaderStyles}
                showClose
                closeAriaLabel={t('dialog.action.close')}
                onClose={resetDialogAndClose}
            >
                <Typography css={getCancelDialogHeaderTitleStyles} variant="heading4" component="h4">
                    {isSuccess
                        ? t('subscriptions.cancellation-confirmation.title')
                        : showDiscountDialog
                          ? discountApplied
                              ? t('subscriptions.cancellation-offer-success.title', { planName })
                              : t('subscriptions.cancellation-offer.title')
                          : t('subscriptions.cancellation-form.title')}
                </Typography>
            </DialogHeader>

            {isSuccess ? (
                <SubscriptionCancellationSuccess
                    planName={planName}
                    expirationDate={expirationDate}
                    onClose={resetDialogAndClose}
                />
            ) : showDiscountDialog ? (
                discountApplied ? (
                    <RetentionDiscountSuccess
                        close={resetDialogAndClose}
                        discountedPrice={discountedPrice}
                        startDate={startDate ?? ''}
                        fullPrice={fullPrice}
                    />
                ) : (
                    <RetentionDiscount
                        cancel={cancelClientSubscription}
                        applyRetentionDiscount={applyRetentionDiscount}
                        discountedPrice={discountedPrice}
                        discountValue={discountValue}
                        pendingApplication={pendingApplication}
                        discountCurrency={discountCurrency}
                    />
                )
            ) : (
                <SubscriptionCancellationConfirmation
                    planName={planName}
                    onClose={resetDialogAndClose}
                    setShowDiscountDialog={setShowDiscountDialog}
                    cancel={cancelClientSubscription}
                    isPending={isPending}
                />
            )}
        </Dialog>
    );
}
